<template>
  <div class="ProfessionAndActivityForm">
    <h2 class="gl_form-title">
      {{ t('copy_life_professions-and-activities-form_title') }}
    </h2>

    <BaseAutocompleteInputStrict
      id="address"
      icon="icon-maletin-comercial"
      :label="t('copy_life_professions-and-activities-form_professions-question')"
      v-model="profession"
      :items="professions.map(({ label }) => label)"
      :placeholder="t('common.select.placeholder')"
      :rules="[required]"
      :editable="riskChecked === RiskCheckedStateEnum.NONE"
    />

    <CardInfoComponent
      v-if="isProfessionLowRisk"
      :icon="configurations.tags.professionLowRiskAlert.icon"
    >
      {{ configurations.tags.professionLowRiskAlert.label }}
    </CardInfoComponent>

    <CardInfoComponent
      v-else-if="isProfessionHighRisk"
      :icon="professionRiskIcon"
    >
      {{
        hasMediator ? professionRiskMediatorText : configurations.tags.professionHighRiskAlert.label
      }}
    </CardInfoComponent>

    <FormField :statement="t('copy_life_professions-and-activities-form_activities-question')">
      <BaseSelect
        id="payment-frequency"
        :items="activities"
        v-model="activityCode"
        :disabled="riskChecked !== RiskCheckedStateEnum.NONE"
      />
    </FormField>

    <CardInfoComponent
      v-if="isActivityHighRisk"
      :icon="activityRiskIcon"
    >
      {{ hasMediator ? activityRiskMediatorText : configurations.tags.activityHighRiskAlert.label }}
    </CardInfoComponent>

    <template
      v-if="riskChecked === RiskCheckedStateEnum.ALLOW_WITH_PRICE_RISE && askToCalculateInsurance"
    >
      <p class="price-rise-title">
        <span>{{ t('hiring.common.holder-insured', { n: 1 }) }}</span>
        <span>{{ format(insured.userDetails.birthDate) }}</span>
      </p>

      <div
        v-if="professionPriceRice"
        class="price-rise-block"
      >
        <p>{{ t('copy_summary-step_profession-data_profession-label') }}</p>
        <p>
          <span>{{ profession }}</span>
          <span>{{ t('copy_summary-step_profession-data_profession-label-risk') }}</span>
        </p>
      </div>

      <div
        v-if="activityPriceRice"
        class="price-rise-block"
      >
        <p>{{ t('copy_summary-step_profession-data_activity-label') }}</p>
        <p>
          <span>{{ activity }}</span>
          <span>{{ t('copy_summary-step_profession-data_profession-label-risk') }}</span>
        </p>
      </div>

      <v-divider class="divider-blue mb-2" />

      <TotalAmountComponent
        :label="t('hiring.common.total-amount.total')"
        :amount="price"
        :currency="paymentUnits"
        :period="`/${period}`"
      />
    </template>

    <div
      v-if="riskChecked !== RiskCheckedStateEnum.NONE"
      class="mt-3"
    >
      <p class="light">{{ t('copy_change-data-before-validate') }}</p>

      <BaseButton
        class="my-3"
        type="transparent"
        @click="riskChecked = RiskCheckedStateEnum.NONE"
      >
        {{ t('copy_button_return-and-change') }}
      </BaseButton>
    </div>

    <v-divider class="my-5" />

    <div class="gl_form-actions">
      <BaseButton
        v-if="riskChecked === RiskCheckedStateEnum.NONE"
        @click="checkRisk"
      >
        {{ t('copy_button_continue') }}
      </BaseButton>

      <BaseButton
        v-if="riskChecked === RiskCheckedStateEnum.DISALLOW"
        @click="hasMediator ? callTelNumber() : openPhoneModal()"
        v-bind="!hasMediator ? { atphone: '' } : {}"
        :class="{ atphone: !hasMediator }"
      >
        {{
          hasMediator
            ? t(`common.call-to`, { phone: mediatorPhone })
            : t('copy_button_continue-by-phone')
        }}
      </BaseButton>

      <BaseButton
        v-if="riskChecked === RiskCheckedStateEnum.ALLOW_WITH_PRICE_RISE"
        @click="repricing"
      >
        {{ t('copy_button_accept-and-continue') }}
      </BaseButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'
  import TotalAmountComponent from '@/modules/common/hiring/components/TotalAmountComponent.vue'
  import { useRepricing } from '@/modules/common/hiring/composables/useRepricing'
  import BaseAutocompleteInputStrict from '@/modules/core/components/ui/BaseAutocompleteInputStrict.vue'
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import BaseSelect from '@/modules/core/components/ui/BaseSelect.vue'
  import FormField from '@/modules/core/components/utils/FormField.vue'
  import { useErrorManagerComposable } from '@/modules/core/composables/errorManagerComposable'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { useCommonStore } from '@/modules/core/stores'
  import { useLoadingStore } from '@/modules/core/stores/loadingStore'
  import { useSupportModalStore } from '@/modules/core/stores/supportModalStore'
  import { useProfessions } from '@/modules/life/common/composables/useProfessionsComposable'
  import type LifeConfigurations from '@/modules/life/common/models/APP/LifeConfigurations'
  import type LifeInsurance from '@/modules/life/common/models/APP/LifeInsurance'
  import LifeHiringAnswers from '@/modules/life/hiring/models/APP/LifeHiringAnswers'
  import { useLifeStore } from '@/modules/life/hiring/stores'
  import { format } from '@/utils/date.utils'
  import { computed, inject, onMounted, onUnmounted, ref, watch, type Ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  const { t } = useI18n()
  const { required } = useRules()
  const lifeStore = useLifeStore()
  const loadingStore = useLoadingStore()
  const supportModalStore = useSupportModalStore()
  const errorManager = useErrorManagerComposable()
  const commonStore = useCommonStore()

  defineExpose({ triggerSubmit })
  const validateForm = inject<() => Promise<boolean>>('validateForm') ?? (async () => true)

  interface PropsInterface {
    nextForm: String[]
    answers: LifeHiringAnswers
    configurations: LifeConfigurations
    items?: { label: string; value: string }[] // Add items property
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})

  const emit = defineEmits(['repricing', 'next'])

  const repricingAnswers = ref<LifeHiringAnswers>(
    new LifeHiringAnswers(props.answers)
  ) as Ref<LifeHiringAnswers>

  const insured = props.answers.insureds[0]

  const hasMediator = computed(() => !!commonStore.infoMediator?.mediatorId)
  const mediatorPhone = computed(() => commonStore.infoMediator?.mediatorPhone || '')
  const professionRiskIcon = computed(() =>
    hasMediator.value
      ? props.configurations.tags.professionHighRiskAlertMediator?.icon ||
        props.configurations.tags.professionHighRiskAlert.icon
      : props.configurations.tags.professionHighRiskAlert.icon
  )
  const activityRiskIcon = computed(() =>
    hasMediator.value
      ? props.configurations.tags.activityHighRiskAlertMediator?.icon ||
        props.configurations.tags.activityHighRiskAlert.icon
      : props.configurations.tags.activityHighRiskAlert.icon
  )
  const professionRiskMediatorText = computed(
    () =>
      props.configurations.tags.professionHighRiskAlertMediator?.label ||
      props.configurations.tags.alertRiskProfessionMediator ||
      t('hiring.life.alert-risk-profession-mediator')
  )
  const activityRiskMediatorText = computed(
    () =>
      props.configurations.tags.activityHighRiskAlertMediator?.label ||
      props.configurations.tags.alertRiskActivityMediator ||
      t('hiring.life.alert-risk-activity-mediator')
  )

  const {
    NONE_VALUE,
    RiskCheckedStateEnum,

    professionCode,
    activityCode,
    professions,
    activities,
    riskChecked,
    isProfessionHighRisk,
    isProfessionLowRisk,
    isActivityHighRisk,
    professionPriceRice,
    activityPriceRice,
    profession,
    activity,
    skipRiskChecking,
    getRiskCheckedState,
    resetAll: resetProfessions
  } = useProfessions(props.answers)

  const {
    price,
    period,
    paymentUnits,
    askToCalculateInsurance,
    initializeCommon,
    resetAll,
    simulateRepricingCommon,
    repricingCommon
  } = useRepricing<LifeHiringAnswers, LifeInsurance>(repricingAnswers, props.configurations)

  watch(
    () => professionCode.value,
    (value: string) => {
      repricingAnswers.value.insureds[0].profession = value
    }
  )

  watch(
    () => activityCode.value,
    (value: string) => {
      repricingAnswers.value.insureds[0].activity = value
    }
  )

  async function checkRisk() {
    const valid = await validateForm()
    if (!valid) {
      return
    }

    if (skipRiskChecking()) {
      emit('next')
      return
    }

    try {
      loadingStore.startLoading('common.loading.processing')

      const [onlyProfession, onlyActivity, both] = await Promise.all([
        lifeStore.getIncrementRisk(Number(professionCode.value), Number(NONE_VALUE)),
        lifeStore.getIncrementRisk(Number(NONE_VALUE), Number(activityCode.value)),
        lifeStore.getIncrementRisk(Number(professionCode.value), Number(activityCode.value))
      ])
      isProfessionLowRisk.value = onlyProfession.allowsHiring && onlyProfession.priceRice
      isProfessionHighRisk.value = !onlyProfession.allowsHiring
      professionPriceRice.value = onlyProfession.allowsHiring && onlyProfession.priceRice

      isActivityHighRisk.value = !onlyActivity.allowsHiring
      activityPriceRice.value = Boolean(onlyActivity.amountPriceRice)
      riskChecked.value = getRiskCheckedState(both.allowsHiring, both.priceRice)

      if (riskChecked.value === RiskCheckedStateEnum.ALLOW_WITHOUT_PRICE_RISE) {
        const firstInsured = props.answers.insureds[0]
        const sameProfessionAsSaved = firstInsured.profession === professionCode.value
        const sameActivityAsSaved = firstInsured.activity === activityCode.value
        if (sameProfessionAsSaved && sameActivityAsSaved) {
          loadingStore.stopLoading()
          emit('next')
        } else {
          await repricing()
        }
      } else if (riskChecked.value === RiskCheckedStateEnum.ALLOW_WITH_PRICE_RISE) {
        await simulateRepricing()
        loadingStore.stopLoading()
      } else {
        loadingStore.stopLoading()
      }
    } catch (error) {
      loadingStore.stopLoading()
      errorManager.handleErrorInsight(error)
    }
  }

  function openPhoneModal() {
    supportModalStore.setOpenModal(true)
  }

  function callTelNumber() {
    window.location.href = `tel:${mediatorPhone.value.replace(/\s/g, '')}`
  }

  async function simulateRepricing() {
    try {
      await simulateRepricingCommon(lifeStore.getNewRaterPrice)
    } catch (error) {
      errorManager.handleErrorInsight(error)
    }
  }

  async function repricing() {
    try {
      await repricingCommon(lifeStore.getNewRaterPrice, lifeStore.hireProduct)
      emit('repricing', { goToNextPage: true })
    } catch (error) {
      errorManager.handleErrorInsight(error)
    }
  }

  function triggerSubmit() {
    const page = props.nextForm[0]

    return { page }
  }

  function reset(
    eventData: CustomEventInit<{
      answers: LifeHiringAnswers
    }>
  ) {
    resetProfessions()
    resetAll(eventData.detail!.answers)
  }

  async function mounted() {
    try {
      loadingStore.stopLoading()
      const [_professions, _activities] = await Promise.all([
        lifeStore.fetchProfessions(),
        lifeStore.fetchActivities()
      ])

      professions.value = _professions
      activities.value = _activities

      document.addEventListener('life-repricing-step-0', reset)

      initializeCommon(props.answers)
    } catch (error) {
      loadingStore.stopLoading()
    }
  }

  onMounted(mounted)
  onUnmounted(() => {
    document.removeEventListener('life-repricing-step-0', reset)
  })
</script>

<style lang="scss" scoped>
  .ProfessionAndActivityForm {
    p.light {
      color: $color-neutral;
      text-align: center;
    }

    .price-rise-block {
      color: $color-neutral;
      margin-bottom: $spacing-m;

      p {
        display: flex;
        justify-content: space-between;
        gap: $gap-xs;

        span {
          color: $color-black-200;
        }
      }
    }

    .price-rise-title {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: $gap-xs;
      margin-bottom: $spacing-m;
      color: $color-black-200;

      span:first-child {
        color: $color-primary-dark;
        font-weight: $font-weight-600;
        background-color: $color-primary-lighter;
        padding: $spacing-xs $spacing-m;
        border-radius: $b-radius-50;
        border: 1px solid $color-blue-200;
      }
    }
  }
</style>
